import { useState, useEffect, useCallback } from 'react';
import {
  fetchBookInfo,
  fetchBookSections,
  fetchNotes,
  fetchBookEntities,
  saveNote,
  deleteNote,
  saveEntity,
  fetchBookSection
} from '../services/api';

export const useBookData = (readingKey) => {
  const [reading, setReading] = useState(null);
  const [sections, setSections] = useState([]);
  const [toc, setToc] = useState([]);
  const [allNotes, setAllNotes] = useState([]);
  const [entities, setEntities] = useState([]);
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [sectionHtml, setSectionHtml] = useState('');
  
  const loadBookData = useCallback(async () => {
    try {
      const [info, sectionsData, notesData, entitiesData] = await Promise.all([
        fetchBookInfo(readingKey),
        fetchBookSections(readingKey),
        fetchNotes(readingKey),
        fetchBookEntities(readingKey),
      ]);
      
      setReading(info);
      setSections(sectionsData.sections || []);
      setToc(sectionsData.toc || []);
      setAllNotes(notesData.notes || []);
      setEntities(entitiesData.entities || []);
      
      // Restore last read section
      const savedSections = JSON.parse(localStorage.getItem('lastReadSections') || '{}');
      const savedIndex = savedSections[readingKey];
      if (savedIndex !== undefined && savedIndex >= 0 && savedIndex < (sectionsData.sections || []).length) {
        setCurrentSectionIndex(savedIndex);
      }
    } catch (error) {
      console.error("Failed to load book data", error);
    }
  }, [readingKey]);

  const loadSectionContent = useCallback(async () => {
    if (!sections.length) return;
    const currentSection = sections[currentSectionIndex];
    if (!currentSection) return;
    
    try {
      const html = await fetchBookSection(readingKey, currentSection.id);
      setSectionHtml(html);
      
      // Save last read section
      const savedSections = JSON.parse(localStorage.getItem('lastReadSections') || '{}');
      savedSections[readingKey] = currentSectionIndex;
      localStorage.setItem('lastReadSections', JSON.stringify(savedSections));
    } catch (error) {
      console.error("Failed to load section content", error);
    }
  }, [readingKey, sections, currentSectionIndex]);

  const addNote = async (noteData) => {
    const response = await saveNote(readingKey, noteData);
    const savedNote = response.note;
    setAllNotes(prev => [...prev, savedNote]);
    return savedNote;
  };

  const removeNote = async (noteId) => {
    await deleteNote(readingKey, noteId);
    setAllNotes(prev => prev.filter(n => n.id !== noteId));
  };

  const addEntity = async (entityData) => {
    const response = await saveEntity(readingKey, entityData);
    const savedEntity = response.entity;
    setEntities(prev => {
      const alreadyTagged = prev.some(
        e => e.type === savedEntity.type && e.title.toLowerCase() === savedEntity.title.toLowerCase()
      );
      return alreadyTagged ? prev : [...prev, savedEntity];
    });
    return savedEntity;
  };

  useEffect(() => {
    loadBookData();
  }, [loadBookData]);

  useEffect(() => {
    loadSectionContent();
  }, [loadSectionContent]);

  const goToSection = (index) => {
    if (index >= 0 && index < sections.length) {
      setCurrentSectionIndex(index);
    }
  };

  const goToNextSection = () => goToSection(currentSectionIndex + 1);
  const goToPreviousSection = () => goToSection(currentSectionIndex - 1);

  return {
    reading,
    sections,
    toc,
    allNotes,
    entities,
    currentSectionIndex,
    sectionHtml,
    addNote,
    removeNote,
    addEntity,
    goToSection,
    goToNextSection,
    goToPreviousSection
  };
};

